import { FontIcon, ILinkProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLink, IPStack, IPText } from 'baseComponents';
import { useWidgetDrillDownDisabled } from 'components/Dashboard/WidgetDrillDownContext';
import { globalStackTokensGapExtraSmall, openExtIcon } from 'globalStyles';
import Entity from 'models/entity';
import { DirectoryRef } from 'models/identityProvider';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getEntityUrl, getExternalUrl, navigateToExternalUrl } from 'utils/url';

export interface HrefLinkProps extends ILinkProps {
  /**
   * If `href` is not provided, this entity will be used to generate the URL.
   * This passes the entity URL to the underlying Link component as its `href` prop.
   */
  entity?: Entity;

  /**
   * If provided, the url will be converted to an external link.
   * If enabled, `includeContext` `directory`, and `orgUnitId` will be used to build the external URL, if also provided.
   */
  external?: boolean;

  /**
   * If true, opens the link in a new tab.
   */
  newTab?: boolean;

  /**
   * If `external` is true, this will include the directory and tenantId from the appContext's user in the URL,
   * unless overwritten by properties: `directory` or `orgUnitId`.
   */
  includeContext?: boolean;

  /**
   * Overwrites the directory (the tenant to sign in to) in the URL if `external` and `includeContext` are true.
   */
  directory?: DirectoryRef;

  /**
   * Overwrites the orgUnitId in the URL if `external` and `includeContext` are true.
   */
  orgUnitId?: string;

  /**
   * Shows the new tab icon before any children.
   */
  showNewTabIcon?: boolean;

  children?: React.ReactNode;
}

const HrefLink = ({
  children,
  onClick,
  entity,
  external,
  newTab,
  includeContext,
  href,
  directory,
  orgUnitId,
  showNewTabIcon,
  ...rest
}: HrefLinkProps): React.ReactElement => {
  const appContext = useContext(AppContext);
  const history = useHistory();
  const drillDownDisabled = useWidgetDrillDownDisabled();
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [externalUrl, setExternalUrl] = useState<string | undefined>(undefined);

  //on a per-customer (Tenant-type) dashboard, internal route drill-downs resolve in the
  //partner's own tenant, so disable them; modal openers (onClick) and explicit external/newTab links keep working
  const isDisabledDrillDown = drillDownDisabled && !!url && !onClick && !external && !newTab;

  //
  // Effects
  //
  useEffect(() => {
    const currentUrl = getUrl();

    if (!currentUrl) {
      setUrl(undefined);

      return;
    }

    setUrl(currentUrl);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, href]);

  useEffect(() => {
    if (url && external) {
      const urlDirectory = includeContext ? directory ?? appContext.user.tenant : undefined;
      const urlOrgUnitId = includeContext ? orgUnitId ?? appContext.user.login.tenantId : '';
      const newExternalUrl = getExternalUrl(url, urlDirectory, urlOrgUnitId);

      setExternalUrl(newExternalUrl);
    } else if (url) {
      setExternalUrl(url);
    } else {
      setExternalUrl(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, external]);

  const getUrl = (): string | undefined => {
    let url = undefined;

    if (entity) {
      url = getEntityUrl(entity);
    } else if (href) {
      url = href;
    }

    return url;
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLElement>) => {
    // Allows us to use the link as a button, while still allowing right-click "open link in new tab" behavior.
    e.preventDefault();

    if (isDisabledDrillDown) {
      return;
    }

    if (onClick) {
      onClick(e);
    } else if (url) {
      if (external) {
        const urlDirectory = includeContext ? directory ?? appContext.user.tenant : undefined;
        const urlOrgUnitId = includeContext ? orgUnitId ?? appContext.user.login.tenantId : '';
        navigateToExternalUrl(url, urlDirectory, urlOrgUnitId);
      } else if (newTab) {
        window.open(url, '_blank', 'noreferrer');
      } else {
        history.push(url);
      }
    }
  };

  if (isDisabledDrillDown) {
    return <IPText>{children}</IPText>;
  }

  return (
    <IPLink {...rest} href={externalUrl} onClick={handleClick}>
      {showNewTabIcon ? (
        <IPStack horizontal tokens={globalStackTokensGapExtraSmall} verticalAlign="start">
          <IPStack.Item>
            <FontIcon {...openExtIcon} />
          </IPStack.Item>
          <IPStack.Item>{children}</IPStack.Item>
        </IPStack>
      ) : (
        children
      )}
    </IPLink>
  );
};

export default HrefLink;
